import { clients } from 'lib/https';
import { ConfigurationSettings } from '/models/configuration-settings';

export interface IamData {
  itemId: string;
  createdDate: string;
  lastUpdatedDate: string;
  lastLoggedInTime: string;
  language: string;
  salutation: string;
  firstName: string;
  lastName: string | null;
  email: string;
  userName: string;
  phoneNumber: string | null;
  country?: string;
  roles: string[];
  permissions: string[];
  active: boolean;
  isVarified: boolean;
  profileImageUrl: string | null;
  mfaEnabled: boolean;
  organizations: Array<{
    itemId: string;
    name: string;
  }>;
  plan: {
    itemId: string;
    name: string;
  };
  planItem: {
    itemId: string;
    title: string | null;
  };
  groups: UserGroup[];
}

export interface UserGroup {
  itemId: string;
  name: string;
}

export interface UserFilter {
  email?: string;
  name?: string;
  organization?: string;
  userIds?: string[];
  excludeGroupId?: string;
  IsOrgSpecific?: boolean;
  organizationIds?: string[];
}

export interface GetUsersPayload {
  page: number;
  pageSize: number;
  filter?: UserFilter;
}

const normalizeOrganizationFilter = (filter: UserFilter | undefined): string[] => {
  if(!filter) return [];

  if(Array.isArray(filter.organizationIds) && filter.organizationIds.length > 0) {
    return filter.organizationIds;
  }

  if(filter.organization && typeof(filter.organization) === 'string') {
    return [filter.organization];
  }

  return [];
};

export const getUsers = (payload: GetUsersPayload, config: ConfigurationSettings) => {
  const requestBody = {
    page: payload.page,
    pageSize: payload.pageSize,
    filter: {
      email: payload.filter?.email ?? '',
      name: payload.filter?.name ?? '',
      userIds: payload.filter?.userIds ?? [],
      excludeGroupId: payload.filter?.excludeGroupId ?? '',
      IsOrgSpecific: payload.filter?.IsOrgSpecific ?? false,
      organizationIds: normalizeOrganizationFilter(payload.filter)
    },
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.getUsers}`;
  return clients.post<{
    data: IamData[];
    totalCount: number;
  }>(url, config.blocksKey, JSON.stringify(requestBody));
};

export interface RoleData {
  id: string;
  name: string;
  description: string;
  createdDate: string;
  lastUpdatedDate: string;
  permissions: string[];
  isDefault: boolean;
  active: boolean;
}

export interface RoleSort {
  property: string;
  isDescending: boolean;
}

export interface RoleFilter {
  search?: string;
}

export interface GetRolesPayload {
  page: number;
  pageSize: number;
  sort?: RoleSort;
  filter?: RoleFilter;
  projectKey?: string;
}

/**
 * Function to fetch a list of roles from the API with pagination, sorting, and filtering.
 *
 * @param {GetRolesPayload} payload - The payload containing pagination, sorting, and filter options.
 * @returns {Promise<{ data: RoleData[], totalCount: number }>} - A promise that resolves to an object containing the list of roles and the total count.
 */
export const getRoles = (payload: GetRolesPayload, config: ConfigurationSettings) => {
  const requestBody = {
    page: payload.page,
    pageSize: payload.pageSize,
    sort: payload.sort
      ? {
          property: payload.sort.property,
          isDescending: payload.sort.isDescending,
        }
      : undefined,
    filter: {
      search: payload.filter?.search ?? '',
    },
    projectKey: config.blocksKey,
  };
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.getRoles}`;

  return clients.post<{
    data: RoleData[];
    totalCount: number;
  }>(url, config.blocksKey, JSON.stringify(requestBody));
};

export interface CreateBulkUsersPayload {
  emails: string[];
  roles: string[];
  organizationIds: string[];
}

/**
 * Function to create multiple users in bulk via the CreateBulk API.
 *
 * @param {CreateBulkUsersPayload} payload - The payload containing emails, roles, and organization IDs.
 * @param {ConfigurationSettings} config - The configuration settings containing API paths and blocksKey.
 * @returns {Promise<{ isSuccess: boolean, errors?: unknown }>} - A promise that resolves to the bulk creation result.
 */
export const createBulkUsers = (payload: CreateBulkUsersPayload, config: ConfigurationSettings) => {
  const baseUrl = `https://${config.paths.baseUrl}`;
  const url = `${baseUrl}${config.paths.iam.createBulkUser}`;

  return clients.post<{
    isSuccess: boolean;
    errors?: unknown;
  }>(url, config.blocksKey, JSON.stringify(payload));
};

export function compareValues(a: number, b: number) {
  if (a < b) {
    return -1;
  } else if (a > b) {
    return 1;
  } else {
    return 0;
  }
}

export const createGroup = async (
    data: any,
    config: ConfigurationSettings
) => {

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.bebrave.addOrUpdateUserGroup}`;

    const payload = {
        ...data,
        ProjectKey: config.blocksKey,
    };
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};

export const getGroupsAndUsers = async (
    data: any,
    config: ConfigurationSettings
) => {

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.bebrave.getGroupsAndUsers}`;

    const payload = {
        ...data
    };
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};


export const getGroupById = async (
    data: any,
    config: ConfigurationSettings
) => {
    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.bebrave.getGroupById}`;
    return clients.post(url, config.blocksKey, JSON.stringify(data));
};


export const removeUserFromGroup = async (
    data: any,
    config: ConfigurationSettings
) => {

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.bebrave.removeUserFromGroup}`;

    const payload = {
        ...data
    };
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};


export const deleteIamGroup = async (
    data: any,
    config: ConfigurationSettings
) => {

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.bebrave.deleteGroup}?ItemId=${data.groupId}`;

    return clients.delete(url, config.blocksKey);
};

export const getGroupsByIds = async (
    data: any,
    config: ConfigurationSettings
) => {

    const baseUrl = `https://${config.paths.baseUrl}`;
    const url = `${baseUrl}${config.paths.bebrave.getGroupsByIds}`;

    const payload = {
        ...data
    };
    return clients.post(url, config.blocksKey, JSON.stringify(payload));
};